import { httpsCallable } from 'firebase/functions';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Language, Languages } from '../../common/CountryMarketConfig';
import { CustomerResult } from '../../common/Customer';
import { Dropdown } from '../../components/Dropdown';
import { InputGroup } from '../../components/InputGroup';
import { Link } from '../../components/Link';
import { Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal';
import { functions } from '../../firebase';
import { GTMBaseContentViewObject } from '../../hooks/GTMUtils';
import { getCustomers } from '../../hooks/queries';
import { useAuth } from '../../hooks/useAuth';
import { useCountryMarketHelpers } from '../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../hooks/useGTM';
import { useLocalizationCookieHandler } from '../../hooks/useLocalizationCookieHandler';
import {
  DropdownOption,
  DropdownOptionsWithUndefined,
} from '../../lib/Dropdown/types';
import { User } from '../../shared/store';
import { TrackingSchema } from '../../types';
import {
  CPCustomer,
  CPCustomerUser,
  RawCPCustomerUser,
  useCustomerState,
} from '../Briefing/CustomerState';

export const convertEndpointDataToCustomers = (
  data: CustomerResult[],
  user: User,
): CPCustomer[] =>
  data
    .filter(({ error }) => !error)
    .map(({ customers, marketId }) =>
      // TODO: Remove eslint ignore once CustomerResult type has been cleaned up
      // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion, @typescript-eslint/no-non-null-assertion
      customers!!.data.map(
        (customer) =>
          ({
            name: customer.attributes.name,
            marketCountryName: user.countries_and_markets?.[parseInt(marketId)],
            marketId,
            customerId: customer.id,
            customerUsers: customer.relationships.users.data.map(
              ({ id }) => id,
            ),
            am_email: customer.attributes.am_email,
            erpId: customer.attributes.customer_erp_id,
          } as CPCustomer),
      ),
    )
    .flat()
    .filter(({ customerUsers }) => customerUsers.length);

export const CustomerUserSelection: FC = () => {
  // This handles the storing and retrieving of selected customer and customerUser
  const [
    { cpCustomer, cpCustomerUser, isSelectionConfirmed },
    {
      setCPCustomer,
      setCustomerId,
      setCPCustomerUser,
      setIsSelectionConfirmed,
      setCustomerUser,
    },
  ] = useCustomerState();

  const {
    existsCountryMarketConfigByString,
    findCountryMarketConfigByString,
    redirectTargetByCountryMarket,
  } = useCountryMarketHelpers();

  // These store the customers and customerUsers that need to be visible in the modal dropdowns
  const [availableCustomers, setAvailableCustomers] = useState<CPCustomer[]>(
    [],
  );
  const [availableCPCustomerUsers, setAvailableCPCustomerUsers] = useState<
    CPCustomerUser[]
  >([]);
  const [availableLanguages, setAvailableLanguages] = useState<Languages>();
  const [availableLanguageOptions, setAvailableLanguageOptions] = useState<
    DropdownOption[]
  >([]);

  // Keep track what things need to finish loading before the CustomerUser modal can be displayed
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingCustomerUsers, setLoadingCustomerUsers] = useState(false);
  const [settingCustomerUser, setSettingCustomerUser] = useState(false);
  const [errorMessage, setErrorMessage] = useState<
    'loadingCustomer' | 'loadingCustomerUser' | 'settingCustomerUser'
  >();

  const [currentCustomerIdUsed, setCurrentCustomerIdUsed] = useState<string>();
  const [currentMarketIdUsed, setCurrentMarketIdUsed] = useState<string>();
  const [currentCustomerMarket, setCurrentCustomerMarket] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState<Language>();

  const { t } = useTranslation();
  const { changeAppLanguage } = useLocalizationCookieHandler();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [{ user, displayUser }, { signOut }] = useAuth();
  const userIsAzelisEmployee = useMemo(() => user?.azelis_employee, [user]);

  const { updateDataLayer } = useGTM();

  const trackImpersonatorSwitch = async (displayUser: User) => {
    const userCustomers = await getCustomers(displayUser);

    // current assumption is that only customerUsers having only one customer will ever get impersonated
    const currentCustomer =
      displayUser.customers &&
      userCustomers.find(({ erp_id }) => erp_id === displayUser.customers[0]);

    updateDataLayer({
      event: 'Impersonator Switch',
      schema: TrackingSchema.impersonator,
      data: {
        asCustomerName: currentCustomer?.customer_name,
        asCustomerGUID: currentCustomer?.GUID,
        asCustomerUserGUID: displayUser.GUID,
      },
    });
  };

  useEffect(() => {
    if (
      isSelectionConfirmed ||
      availableCustomers.length ||
      !user?.countries_and_markets
    ) {
      return;
    }

    // Filter the marketsegments and only get info for the ones that elab supports currently
    const filteredUserMarketIds = Object.values(
      user.countries_and_markets,
    ).filter(existsCountryMarketConfigByString);

    // Translate it back to the numeric ids
    const filteredNumericMarketKeys = Object.entries(user.countries_and_markets)
      .filter(([, value]) => filteredUserMarketIds.includes(value))
      .map(([key]) => key);

    if (
      // If the user is an admin user
      userIsAzelisEmployee &&
      // and the user hasn't yet confirmed the customer/customerUser they want, or pressed cancel,
      !isSelectionConfirmed &&
      // and there aren't yet any available customers,
      availableCustomers.length === 0 &&
      // and there are market keys to pass to the cloud function,
      filteredNumericMarketKeys.length > 0 &&
      // and this process hasn't already started...
      loadingCustomers
    ) {
      httpsCallable(
        functions,
        'getCustomersForMarkets',
      )(filteredNumericMarketKeys)
        .then(({ data }: { data: any }) => {
          if (data.error) {
            console.log('something went wrong getting customers', data.error);
            setErrorMessage('loadingCustomer');
            setLoadingCustomers(false);
            return false;
          }

          // Extract the necessary data from the customer records returned
          const processedCustomers = convertEndpointDataToCustomers(data, user);

          setAvailableCustomers(processedCustomers);
          setLoadingCustomers(false);
        })
        .catch((e) => {
          setLoadingCustomers(false);
          console.error(e);
          setErrorMessage('loadingCustomer');
          return false;
        });
    }
  }, [
    setAvailableCustomers,
    setLoadingCustomers,
    user,
    userIsAzelisEmployee,
    loadingCustomers,
    availableCustomers,
    isSelectionConfirmed,
    existsCountryMarketConfigByString,
  ]);

  useEffect(() => {
    if (
      // If the user hasn't yet confirmed the customer/customerUser they want, or pressed cancel,
      !isSelectionConfirmed &&
      // and the user has selected a customer,
      cpCustomer?.customerId &&
      // and there are the same market and customer selected,
      (cpCustomer.customerId !== currentCustomerIdUsed ||
        cpCustomer.marketId !== currentMarketIdUsed) &&
      // and this process isn't already ongoing...
      !loadingCustomerUsers
    ) {
      setLoadingCustomerUsers(true);
      httpsCallable<
        { customerId: string; marketId: string },
        { customerUsers: RawCPCustomerUser[] }
      >(
        functions,
        'getCustomerUsersForCustomerByMarket',
      )({
        customerId: cpCustomer.customerId,
        marketId: cpCustomer.marketId,
      })
        .then((res) => {
          if (!res?.data?.customerUsers) {
            console.log('something went wrong getting customerUsers');
            setErrorMessage('loadingCustomerUser');
            return false;
          }

          setAvailableCPCustomerUsers(
            res.data.customerUsers.map((cpcu) => ({
              id: cpcu.id,
              erpId: cpCustomer.erpId,
              customerName: cpCustomer.name,
              firstName: cpcu.attributes.firstName,
              lastName: cpcu.attributes.lastName,
              email: cpcu.attributes.email,
              e_lab_administrator: cpcu.attributes.e_lab_administrator,
            })),
          );
          setCurrentCustomerIdUsed(cpCustomer.customerId);
          setCurrentMarketIdUsed(cpCustomer.marketId);
          setLoadingCustomerUsers(false);
        })
        .catch((e) => {
          console.log(e);
          setErrorMessage('loadingCustomerUser');
          return false;
        });
    }
  }, [
    currentCustomerIdUsed,
    cpCustomer,
    loadingCustomerUsers,
    isSelectionConfirmed,
    currentMarketIdUsed,
  ]);

  const onCustomerChange = useCallback(
    (option: DropdownOptionsWithUndefined) => {
      if (!option || Array.isArray(option)) {
        dispatch(setCPCustomer(null));
        return;
      }

      const [selectedCustomerMarketId, selectedCustomerId] = (
        option.value as string
      )
        .split('-')
        .map((optBit) => optBit.trim());

      const customerFromDropdown = availableCustomers.find(
        ({ customerId, marketId }) =>
          marketId === selectedCustomerMarketId &&
          customerId === selectedCustomerId,
      );

      batch(() => {
        dispatch(setCPCustomer(customerFromDropdown || null));
        dispatch(setCPCustomerUser(null));
      });
    },
    [availableCustomers, dispatch, setCPCustomer, setCPCustomerUser],
  );

  const onCustomerUserChange = useCallback(
    (option: DropdownOptionsWithUndefined) => {
      if (option && !Array.isArray(option)) {
        const selectedCPCustomerId = option.value;
        const cpCustomerUserFromDropdown = availableCPCustomerUsers.find(
          ({ id }) => id === selectedCPCustomerId,
        );
        if (cpCustomerUserFromDropdown) {
          dispatch(setCPCustomerUser(cpCustomerUserFromDropdown));
          return;
        }
      }
      dispatch(setCustomerUser(null));
    },
    [availableCPCustomerUsers, dispatch, setCPCustomerUser, setCustomerUser],
  );

  const onLanguageChange = useCallback(
    (option: DropdownOptionsWithUndefined) => {
      if (option && !Array.isArray(option)) {
        setCurrentLanguage(option.value as Language);
      }
    },
    [],
  );

  const initialCustomer = useMemo(
    () =>
      cpCustomer
        ? {
            name: `${cpCustomer.name} (${cpCustomer.marketCountryName})`,
            value: cpCustomer.customerId,
          }
        : undefined,
    [cpCustomer],
  );

  const initialCustomerUser = useMemo(
    () =>
      cpCustomerUser
        ? {
            value: cpCustomerUser.id,
            name: `${cpCustomerUser.firstName} ${cpCustomerUser.lastName} (${cpCustomerUser.email})`,
          }
        : undefined,
    [cpCustomerUser],
  );

  const initialLanguage = useMemo(
    () =>
      availableLanguageOptions?.length === 1
        ? availableLanguageOptions[0]
        : undefined,
    [availableLanguageOptions],
  );

  const isLoading = useMemo(
    () => loadingCustomers || loadingCustomerUsers || settingCustomerUser,
    [loadingCustomers, loadingCustomerUsers, settingCustomerUser],
  );

  useEffect(() => {
    const languageOptions =
      availableLanguages?.map((language) => ({
        name: t(`labels:languages:${language}`),
        value: language,
      })) || [];
    setAvailableLanguageOptions(languageOptions);
  }, [availableLanguages, t]);

  useEffect(() => {
    if (!cpCustomer) return;

    const countryMarketConfig = findCountryMarketConfigByString(
      cpCustomer.marketCountryName,
    );
    setAvailableLanguages(countryMarketConfig?.languages);

    setCurrentCustomerMarket(cpCustomer.marketCountryName);
  }, [cpCustomer, findCountryMarketConfigByString]);

  // Content to be shown in the Customer/CustomerUser modal
  const specifyUserModalContent = useMemo(
    () =>
      isLoading ? (
        <Loading />
      ) : (
        <>
          <p>{t('modals:customerSelection:content')}</p>
          <div className="mt-4 text-left">
            <InputGroup label={t('modals:customerSelection:form:customer')}>
              <Dropdown
                options={availableCustomers.map((cpCust) => ({
                  value: `${cpCust.marketId}-${cpCust.customerId}`,
                  name: `${cpCust.name} (${cpCust.marketCountryName})`,
                }))}
                initialSelection={initialCustomer}
                typeahead={true}
                typeaheadMinChars={
                  // Dropdown won't work if there are too many availableCustomers
                  availableCustomers.length > 100 ? 1 : undefined
                }
                onChange={onCustomerChange}
              />
            </InputGroup>
          </div>
          <div className="mt-4 text-left">
            <InputGroup label={t('modals:customerSelection:form:customerUser')}>
              <Dropdown
                options={availableCPCustomerUsers.map((cpCustUser) => ({
                  value: cpCustUser.id,
                  name: `${cpCustUser.firstName} ${cpCustUser.lastName} (${cpCustUser.email})`,
                  disabled: cpCustUser.e_lab_administrator,
                }))}
                disabledMessage={`${t(
                  'modals:customerSelection:error:cannotSelectEmployeeUsers',
                )}`}
                initialSelection={initialCustomerUser}
                typeahead={true}
                onChange={onCustomerUserChange}
                disabled={!cpCustomer}
              />
            </InputGroup>
          </div>
          <div className="mt-4 text-left">
            <InputGroup
              label={t('components:countryMarketSelector:preferredLanguage')}
            >
              <Dropdown
                options={availableLanguageOptions}
                onChange={onLanguageChange}
                initialSelection={initialLanguage}
                disabled={(availableLanguages?.length || 0) <= 1}
                triggerOnChangeForInitialSelection={true}
              />
            </InputGroup>
          </div>
        </>
      ),
    [
      availableCPCustomerUsers,
      availableCustomers,
      availableLanguageOptions,
      availableLanguages,
      cpCustomer,
      initialCustomer,
      initialCustomerUser,
      initialLanguage,
      isLoading,
      onCustomerChange,
      onCustomerUserChange,
      onLanguageChange,
      t,
    ],
  );

  useEffect(() => {
    if (!userIsAzelisEmployee || isSelectionConfirmed) return;

    updateDataLayer({
      ...GTMBaseContentViewObject,
      data: {
        category: 'User Area',
        type: 'Select Market/User',
        content: 'Select Customer/User',
      },
    });
    // Needed because of unnessesary rerenders of the app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsAzelisEmployee, isSelectionConfirmed]);

  const footer = useMemo(
    () => (
      <Trans
        i18nKey="modals:customerSelection:footer"
        components={{
          link1: (
            <Link
              onClick={() => {
                signOut();
              }}
            />
          ),
          link2: (
            <Link
              href={process.env.REACT_APP_CP_BASEURL}
              onClick={() => {
                updateDataLayer({
                  event: 'Content Click',
                  schema: TrackingSchema.content,
                  data: {
                    category: 'Customer Portal',
                    type: 'Customer Portal Redirect',
                    content: 'Customer Portal Homepage',
                  },
                });
              }}
            />
          ),
        }}
      />
    ),
    [signOut, updateDataLayer],
  );

  if (errorMessage) {
    return (
      <Modal
        icon="Error"
        heading={t('modals:customerSelection:error:heading')}
        isHidden={!userIsAzelisEmployee || isSelectionConfirmed}
        isComplex={true}
        confirmText={t('modals:customerSelection:error:confirm')}
        onConfirm={() => window.location.reload()}
        cancelText={t('modals:customerSelection:error:cancel')}
        onCancel={signOut}
        footer={footer}
      >
        <p className="mb-3">
          {t(`modals:customerSelection:error:messages:${errorMessage}`)}
        </p>
        <Trans
          i18nKey="modals:customerSelection:error:content"
          components={{
            link1: <Link href="mailto:azelis-elab+support@produktmacher.com" />,
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal
      icon="AccountBehalf"
      heading={t('modals:customerSelection:prompt')}
      /**
       * Don't show if the user is not admin, or if the user has
       * confirmed their selection, either by confirming a customer/customerUser or by clicking cancel,
       */
      isHidden={!userIsAzelisEmployee || isSelectionConfirmed}
      isComplex={true}
      isConfirmButtonDisabled={
        !cpCustomer || !cpCustomerUser || !currentLanguage || isLoading
      }
      confirmText={t('modals:customerSelection:confirm')}
      cancelText={t('modals:customerSelection:cancel')}
      onConfirm={() => {
        setSettingCustomerUser(true);
        httpsCallable(
          functions,
          'getOrCreateCustomerUser',
        )(JSON.stringify(cpCustomerUser))
          .then((res) => {
            currentLanguage && changeAppLanguage(currentLanguage);
            displayUser && trackImpersonatorSwitch(res.data as User);

            // If this customer ID is set, the logged in user must be an Azelis employee
            // thus, we need to differentiate between the creator (the Azelis employee)
            // and the owner (the customer user)
            batch(() => {
              dispatch(setCustomerUser(res.data as User));
              dispatch(setCustomerId(cpCustomer?.erpId ?? null));
              dispatch(setIsSelectionConfirmed(true));
            });

            const url = redirectTargetByCountryMarket(
              currentCustomerMarket,
              'start',
            );

            navigate(url);

            setSettingCustomerUser(false);
          })
          .catch((e) => {
            console.log(e);
            setErrorMessage('settingCustomerUser');
            return false;
          });
      }}
      isCancelHidden
      footer={footer}
    >
      {specifyUserModalContent}
    </Modal>
  );
};
